import { useState, useEffect, useRef } from 'react';
import { Camera, Menu, LogOut, User, Loader2, DollarSign, Lock, Crown, LifeBuoy, X, Home, FolderOpen, Image, Wrench, FileText, CreditCard, Settings, Users, EyeOff, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getAvatarColor } from '@/lib/avatarColors';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { NavLink } from '@/components/NavLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ThemeToggle } from './ThemeToggle';
import { NotificationBell } from './NotificationBell';
import { useHasFeature } from '@/hooks/useHasFeature';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface HeaderProps {
  onLogoClick: () => void;
}

// Auth snapshot type for freezing UI during sign-out
interface AuthSnapshot {
  user: any;
  profile: any;
  isAdmin: boolean;
}

export const Header = ({ onLogoClick }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { user, profile, isLoading, signOut, isAdmin, pricingPreference, updatePricingPreference } = useAuth();
  const { settings, isLoading: settingsLoading } = useSiteSettings();
  const { hasFeature } = useHasFeature();
  const { isLoading: limitsLoading } = usePlanLimits();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Snapshot ref to freeze auth state during sign-out
  const authSnapshotRef = useRef<AuthSnapshot>({ user: null, profile: null, isAdmin: false });
  
  // Update snapshot when user is logged in and not signing out
  useEffect(() => {
    if (user && !isSigningOut) {
      authSnapshotRef.current = { user, profile, isAdmin };
    }
  }, [user, profile, isAdmin, isSigningOut]);
  
  // Use snapshot during sign-out to prevent UI flash
  const effectiveUser = isSigningOut ? authSnapshotRef.current.user : user;
  const effectiveProfile = isSigningOut ? authSnapshotRef.current.profile : profile;
  const effectiveIsAdmin = isSigningOut ? authSnapshotRef.current.isAdmin : isAdmin;
  
  // Keep showing current nav state during signout to prevent flash
  const hasMyGearAccess = hasFeature('my_gear') || isSigningOut;
  const hasRentalPricingAccess = hasFeature('rental_pricing') || isSigningOut;

  const isActiveRoute = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = () => {
    setIsSigningOut(true);
    setMobileMenuOpen(false);
    // Don't await - signOut handles redirect in finally block
    signOut().catch(() => {
      // Reset if somehow signOut returns without redirecting
      setIsSigningOut(false);
    });
  };

  const handleMobileNavClick = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const getInitials = () => {
    if (effectiveProfile?.display_name) {
      return effectiveProfile.display_name.slice(0, 2).toUpperCase();
    }
    if (effectiveUser?.email) {
      return effectiveUser.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <header className={`fixed left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass-strong' : 'bg-transparent'}`} style={{ top: 'var(--announcement-banner-height, 0px)' }}>
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button 
          onClick={onLogoClick}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow overflow-hidden">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain dark:invert" />
            ) : (
              <Camera className="w-4 h-4 text-primary-foreground" />
            )}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            {settings.site_name ? (
              <span className="text-lg font-semibold">
                {settings.site_name.split('.')[0]}<span className="text-primary">.{settings.site_name.split('.')[1]}</span>
              </span>
            ) : (
              <div className="h-5 w-28 rounded bg-gradient-to-r from-muted/30 via-muted/50 to-muted/30 bg-[length:200%_100%] animate-shimmer" />
            )}
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 uppercase tracking-wide">
              Beta
            </Badge>
          </div>
        </button>

        {/* Desktop Nav - only show on xl to prevent overflow */}
        <nav className="hidden xl:flex items-center gap-4">
          {settings.nav_visibility.build && (
            <NavLink 
              to="/" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              activeClassName="text-primary"
            >
              Home
            </NavLink>
          )}
          <NavLink 
            to="/projects"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            activeClassName="text-primary"
          >
            Projects
          </NavLink>
          {settings.nav_visibility.myGear && (
            hasMyGearAccess ? (
              <NavLink 
                to="/my-gear"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                activeClassName="text-primary"
              >
                My Gear
              </NavLink>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      onClick={() => navigate('/pricing')}
                      className="flex items-center gap-1 text-sm font-medium text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                    >
                      <Lock className="w-3 h-3" />
                      My Gear
                      <Crown className="w-3 h-3 text-primary/60" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Upgrade to Pro to access My Gear</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          )}
          {hasRentalPricingAccess ? (
            <NavLink 
              to="/rate-finder"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              activeClassName="text-primary"
            >
              Rate Finder
            </NavLink>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  >
                    <Lock className="w-3 h-3" />
                    Rate Finder
                    <Crown className="w-3 h-3 text-primary/60" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Upgrade to Pro to access Rate Finder</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {settings.nav_visibility.gallery && (
            <NavLink 
              to="/gallery"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              activeClassName="text-primary"
            >
              Gallery
            </NavLink>
          )}
          {settings.nav_visibility.docs && (
            <NavLink 
              to="/docs"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              activeClassName="text-primary"
            >
              Docs
            </NavLink>
          )}
          <NavLink 
            to="/pricing"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            activeClassName="text-primary"
          >
            Pricing
          </NavLink>
          {effectiveIsAdmin && (
            <NavLink 
              to="/admin"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              activeClassName="text-primary"
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-1">
          <ThemeToggle />
          {effectiveUser && <NotificationBell />}
          {isLoading || isSigningOut ? (
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          ) : effectiveUser ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 px-2 hidden sm:flex">
                  <Avatar className="w-7 h-7">
                    <AvatarImage src={effectiveProfile?.avatar_url || undefined} />
                    <AvatarFallback 
                      style={{ 
                        backgroundColor: getAvatarColor(effectiveProfile?.avatar_color).bg, 
                        color: getAvatarColor(effectiveProfile?.avatar_color).text 
                      }}
                      className="text-xs"
                    >
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">
                    {effectiveProfile?.username || effectiveUser.email?.split('@')[0]}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-popover">
                <button
                  onClick={() => navigate('/settings')}
                  className="w-full px-2 py-1.5 text-left rounded-sm hover:bg-accent transition-colors flex items-center gap-2"
                >
                  <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p className="text-sm font-medium truncate">{effectiveProfile?.username || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[180px]">{effectiveUser.email}</p>
                  </div>
                </button>
                <DropdownMenuSeparator />
              {/* Team - available to all authenticated users (they can join teams even without feature) */}
              <DropdownMenuItem onClick={() => navigate('/team')}>
                <Users className="w-4 h-4 mr-2" />
                Team
              </DropdownMenuItem>
              {!isAdmin && (
                <DropdownMenuItem onClick={() => navigate('/support')}>
                  <LifeBuoy className="w-4 h-4 mr-2" />
                  Support
                </DropdownMenuItem>
              )}
                {effectiveIsAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <User className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                      <DollarSign className="w-4 h-4" />
                      <span>Pricing</span>
                    </div>
                    <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-secondary/50 ml-auto">
                      {hasRentalPricingAccess ? (
                        <button
                          onClick={() => updatePricingPreference('rent')}
                          className={`px-2 py-0.5 text-xs rounded transition-colors ${
                            pricingPreference === 'rent'
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Rent
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate('/pricing')}
                          className="flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                        >
                          <Lock className="w-3 h-3" />
                          Rent
                        </button>
                      )}
                      <button
                        onClick={() => updatePricingPreference('buy')}
                        className={`px-2 py-0.5 text-xs rounded transition-colors ${
                          pricingPreference === 'buy'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Buy
                      </button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => updatePricingPreference('hidden')}
                              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                                pricingPreference === 'hidden'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              <EyeOff className="w-3 h-3" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Hide all prices</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="hidden sm:flex" onClick={() => navigate('/auth')}>
                Sign In
              </Button>
              <Button variant="default" size="sm" className="hidden sm:flex" onClick={() => navigate('/auth')}>
                Get Started
              </Button>
            </>
          )}
          
          {/* Mobile Menu - show on smaller than xl screens */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="xl:hidden">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-background p-0">
              <SheetHeader className="p-6 border-b border-border">
                <SheetTitle className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                    {settings.logo_url ? (
                      <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain rounded-lg dark:invert" />
                    ) : (
                      <Camera className="w-4 h-4 text-primary-foreground" />
                    )}
                  </div>
                  <span>{settings.site_name || 'CineRig'}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 uppercase tracking-wide">
                    Beta
                  </Badge>
                </SheetTitle>
              </SheetHeader>
              
              <div className="flex flex-col h-[calc(100%-80px)]">
                {/* Navigation Links */}
                <nav className="flex-1 p-4 space-y-1">
                  {settings.nav_visibility.build && (
                    <button
                      onClick={() => handleMobileNavClick('/')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActiveRoute('/') 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-foreground hover:bg-secondary'
                      }`}
                    >
                      <Home className={`w-5 h-5 ${isActiveRoute('/') ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="font-medium">Home</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleMobileNavClick('/projects')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActiveRoute('/projects') 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-foreground hover:bg-secondary'
                    }`}
                  >
                    <FolderOpen className={`w-5 h-5 ${isActiveRoute('/projects') ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="font-medium">Projects</span>
                  </button>
                  
              {settings.nav_visibility.myGear && (
                <button
                  onClick={() => handleMobileNavClick(hasMyGearAccess ? '/my-gear' : '/pricing')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    !hasMyGearAccess 
                      ? 'text-muted-foreground/50' 
                      : isActiveRoute('/my-gear') 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-foreground hover:bg-secondary'
                  }`}
                >
                  <Wrench className={`w-5 h-5 ${!hasMyGearAccess ? 'text-muted-foreground/50' : isActiveRoute('/my-gear') ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="font-medium flex items-center gap-2">
                    {!hasMyGearAccess && <Lock className="w-3 h-3" />}
                    My Gear
                    {!hasMyGearAccess && <Crown className="w-3 h-3 text-primary/60" />}
                  </span>
                </button>
              )}

              <button
                onClick={() => handleMobileNavClick(hasRentalPricingAccess ? '/rate-finder' : '/pricing')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  !hasRentalPricingAccess 
                    ? 'text-muted-foreground/50' 
                    : isActiveRoute('/rate-finder') 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-foreground hover:bg-secondary'
                }`}
              >
                <Search className={`w-5 h-5 ${!hasRentalPricingAccess ? 'text-muted-foreground/50' : isActiveRoute('/rate-finder') ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="font-medium flex items-center gap-2">
                  {!hasRentalPricingAccess && <Lock className="w-3 h-3" />}
                  Rate Finder
                  {!hasRentalPricingAccess && <Crown className="w-3 h-3 text-primary/60" />}
                </span>
              </button>
                  
                  {settings.nav_visibility.gallery && (
                    <button
                      onClick={() => handleMobileNavClick('/gallery')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActiveRoute('/gallery') 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-foreground hover:bg-secondary'
                      }`}
                    >
                      <Image className={`w-5 h-5 ${isActiveRoute('/gallery') ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="font-medium">Gallery</span>
                    </button>
                  )}
                  
                  {settings.nav_visibility.docs && (
                    <button
                      onClick={() => handleMobileNavClick('/docs')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActiveRoute('/docs') 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-foreground hover:bg-secondary'
                      }`}
                    >
                      <FileText className={`w-5 h-5 ${isActiveRoute('/docs') ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="font-medium">Docs</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleMobileNavClick('/pricing')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActiveRoute('/pricing') 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-foreground hover:bg-secondary'
                    }`}
                  >
                    <CreditCard className={`w-5 h-5 ${isActiveRoute('/pricing') ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="font-medium">Pricing</span>
                  </button>
                  
                  {effectiveIsAdmin && (
                    <button
                      onClick={() => handleMobileNavClick('/admin')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActiveRoute('/admin') 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-foreground hover:bg-secondary'
                      }`}
                    >
                      <Settings className={`w-5 h-5 ${isActiveRoute('/admin') ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="font-medium">Admin</span>
                    </button>
                  )}
                </nav>
                
                {/* User Section */}
                <div className="border-t border-border p-4">
                  {effectiveUser ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => handleMobileNavClick('/settings')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-secondary transition-colors"
                      >
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={effectiveProfile?.avatar_url || undefined} />
                          <AvatarFallback 
                            style={{ 
                              backgroundColor: getAvatarColor(effectiveProfile?.avatar_color).bg, 
                              color: getAvatarColor(effectiveProfile?.avatar_color).text 
                            }}
                            className="text-xs"
                          >
                            {getInitials()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="text-left">
                          <p className="text-sm font-medium">{effectiveProfile?.display_name || 'User'}</p>
                          <p className="text-xs text-muted-foreground truncate">{effectiveUser.email}</p>
                        </div>
                      </button>
                      
                      {/* Team - available to all authenticated users */}
                      <button
                        onClick={() => handleMobileNavClick('/team')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-secondary transition-colors"
                      >
                        <Users className="w-5 h-5 text-muted-foreground" />
                        <span className="font-medium">Team</span>
                      </button>
                      {!effectiveIsAdmin && (
                        <button
                          onClick={() => handleMobileNavClick('/support')}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-secondary transition-colors"
                        >
                          <LifeBuoy className="w-5 h-5 text-muted-foreground" />
                          <span className="font-medium">Support</span>
                        </button>
                      )}
                      
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => handleMobileNavClick('/auth')}
                      >
                        Sign In
                      </Button>
                      <Button 
                        variant="default" 
                        className="w-full" 
                        onClick={() => handleMobileNavClick('/auth')}
                      >
                        Get Started
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
